import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Download, Mail, Trash2, Shield, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { safeLog } from '@/utils/safeLogger';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ExtractionLog = {
  id: string;
  created_at: string;
  sender_email: string | null;
  user_email: string | null;
  extraction_completeness: number | null;
  extraction_success: boolean | null;
  fields_requested: number | null;
  fields_extracted: number | null;
  processing_time_ms: number | null;
  extracted_data: any;
  required_fields: any;
  email_raw_text: string | null;
  email_html: string | null;
  azure_raw_response: string | null;
  error_message: string | null;
  is_test_mail: boolean | null;
  run_id: string | null;
};

export function SignatureDebugLogs() {
  const [logs, setLogs] = useState<ExtractionLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<ExtractionLog | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDebugLoggingEnabled, setIsDebugLoggingEnabled] = useState(false);
  const [isTogglingDebugLog, setIsTogglingDebugLog] = useState(false);
  const [isTriggeringTestMails, setIsTriggeringTestMails] = useState(false);
  const [filter, setFilter] = useState<'all' | 'production' | 'test'>('all');
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [testMailsReceived, setTestMailsReceived] = useState(0);
  const [expectedTestMails, setExpectedTestMails] = useState(0);
  const [testMailCount, setTestMailCount] = useState(100);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingRunIds, setPendingRunIds] = useState<string[]>([]);

  // Counts direkt aus logs berechnen
  const allLogsCount = logs.length;
  const productionLogsCount = logs.filter(l => !l.is_test_mail).length;
  const testLogsCount = logs.filter(l => l.is_test_mail).length;

  useEffect(() => {
    loadLogs();
    loadDebugLoggingStatus();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filter]);

  // Realtime subscription for test mail progress tracking
  useEffect(() => {
    if (!currentRunId) return;

    const channel = supabase
      .channel('test-mails-progress')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'signature_extraction_logs',
          filter: `run_id=eq.${currentRunId}`
        },
        (payload) => {
          console.log(`Test mail received: ${testMailsReceived + 1}/${expectedTestMails}`);
          setTestMailsReceived(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRunId, testMailsReceived, expectedTestMails]);

  // Phase 2: Progress-Toasts alle 10 Sekunden
  useEffect(() => {
    if (!currentRunId || testMailsReceived === 0) return;

    const interval = setInterval(() => {
      if (testMailsReceived > 0 && testMailsReceived < expectedTestMails) {
        const percentage = Math.round((testMailsReceived / expectedTestMails) * 100);
        toast.info(`📬 ${testMailsReceived}/${expectedTestMails} Mails empfangen (${percentage}%)`, {
          id: 'test-mail-progress',
          duration: 10000
        });
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [testMailsReceived, expectedTestMails, currentRunId]);

  // Auto-unlock button when all test mails received
  useEffect(() => {
    if (testMailsReceived > 0 && testMailsReceived === expectedTestMails) {
      toast.success(`✅ Alle ${expectedTestMails} Test-Mails verarbeitet!`);
      setIsTriggeringTestMails(false);
      setCurrentRunId(null);
      setTestMailsReceived(0);
      setExpectedTestMails(0);
    }
  }, [testMailsReceived, expectedTestMails]);

  const loadDebugLoggingStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('admin_settings')
        .select('enable_debug_logging')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading debug logging status:', error);
        return;
      }

      setIsDebugLoggingEnabled(data?.enable_debug_logging || false);
    } catch (error) {
      console.error('Error loading debug logging status:', error);
    }
  };

  const toggleDebugLogging = async () => {
    setIsTogglingDebugLog(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('admin_settings')
        .upsert({
          user_id: user.id,
          enable_debug_logging: !isDebugLoggingEnabled,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setIsDebugLoggingEnabled(!isDebugLoggingEnabled);
      toast.success(
        !isDebugLoggingEnabled 
          ? 'Debug-Logging aktiviert' 
          : 'Debug-Logging deaktiviert'
      );
    } catch (error) {
      console.error('Error toggling debug logging:', error);
      toast.error('Fehler beim Ändern der Debug-Logging Einstellung');
    } finally {
      setIsTogglingDebugLog(false);
    }
  };

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('signature_extraction_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setLogs(data || []);

      const pendingRuns = data
        ?.filter(log => log.is_test_mail && !log.extracted_data)
        .map(log => (log as any).run_id)
        .filter((runId, index, self) => runId && self.indexOf(runId) === index) || [];
      
      setPendingRunIds(pendingRuns);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Fehler beim Laden der Logs');
    } finally {
      setIsLoading(false);
    }
  };

  const analyzePendingLogs = async (runId: string) => {
    setIsAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('batch-analyze-logs', {
        body: { run_id: runId }
      });

      if (error) throw error;

      toast.success(`✅ ${data.successful}/${data.processed} Logs erfolgreich analysiert!`);
      loadLogs();
    } catch (error) {
      console.error('Error analyzing logs:', error);
      toast.error('Fehler bei der Analyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDeleteMyLogs = async () => {
    setIsDeleting(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Keine aktive Sitzung gefunden');
      }

      const { data, error } = await supabase.functions.invoke('delete-my-extraction-logs', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      toast.success(data.message || `${data.deletedCount} Logs erfolgreich gelöscht`);
      setShowDeleteDialog(false);
      loadLogs();
    } catch (error) {
      console.error('Error deleting logs:', error);
      toast.error('Fehler beim Löschen der Logs');
    } finally {
      setIsDeleting(false);
    }
  };

  const triggerTestMails = async () => {
    const runId = `benchmark_${Date.now()}`;
    const mailCount = testMailCount;

    setCurrentRunId(runId);
    setExpectedTestMails(mailCount);
    setTestMailsReceived(0);
    setIsTriggeringTestMails(true);

    try {
      await fetch("https://hooks.zapier.com/hooks/catch/23734535/usbruhb/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "no-cors",
        body: JSON.stringify({
          run_id: runId,
          mails: mailCount,
          seed: 42
        })
      });

      toast.success("✅ Zapier getriggert! Warte auf Verarbeitung...");

      setTimeout(() => {
        if (testMailsReceived === 0 && currentRunId) {
          toast.warning("⚠️ Noch keine Mails empfangen", {
            description: "Zapier Zap läuft möglicherweise nicht. Weitere 4 Minuten Wartezeit."
          });
        }
      }, 60000);

      setTimeout(() => {
        if (testMailsReceived === 0) {
          toast.error("❌ Keine Test-Mails erhalten", {
            description: "Zapier Zap läuft nicht oder `process-test-mails` antwortet nicht"
          });
          setIsTriggeringTestMails(false);
          setCurrentRunId(null);
        } else if (testMailsReceived < mailCount) {
          toast.warning(`⚠️ Nur ${testMailsReceived}/${mailCount} Mails verarbeitet`, {
            description: `${mailCount - testMailsReceived} Mails fehlen. Edge Function Logs prüfen!`
          });
          setIsTriggeringTestMails(false);
          setCurrentRunId(null);
        }
      }, 300000);

    } catch (error) {
      console.error("Error triggering Zapier webhook:", error);
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error("❌ Zapier nicht erreichbar", {
          description: "Webhook-URL prüfen oder Netzwerkverbindung checken"
        });
      } else {
        toast.error("❌ Unbekannter Fehler beim Triggern", {
          description: error instanceof Error ? error.message : "Unbekannter Fehler"
        });
      }
      
      setIsTriggeringTestMails(false);
      setCurrentRunId(null);
    }
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      toast.error('Keine Daten zum Exportieren');
      return;
    }

    const headers = ['Zeitpunkt', 'Sender', 'Erfolg', 'Vollständigkeit', 'Verarbeitungszeit (ms)', 'Felder angefordert', 'Felder extrahiert'];
    const rows = logs.map(log => [
      new Date(log.created_at).toLocaleString('de-DE'),
      log.sender_email || 'N/A',
      log.extraction_success ? 'Ja' : 'Nein',
      `${((log.extraction_completeness || 0) * 100).toFixed(1)}%`,
      log.processing_time_ms?.toString() || 'N/A',
      log.fields_requested?.toString() || 'N/A',
      log.fields_extracted?.toString() || 'N/A'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `extraction-logs-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exportiert');
  };

  const stats = {
    total: logs.length,
    successful: logs.filter(l => l.extraction_success).length,
    avgCompleteness: logs.length > 0
      ? (logs.reduce((sum, l) => sum + (l.extraction_completeness || 0), 0) / logs.length)
      : 0,
    avgProcessingTime: logs.length > 0
      ? (logs.reduce((sum, l) => sum + (l.processing_time_ms || 0), 0) / logs.length)
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Debug-Logging Toggle Card */}
      <Card className="app-tile app-tile-hover">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="app-icon-box">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="app-section-title">Debug-Logging Einstellungen</CardTitle>
              <CardDescription>
                Steuert das Speichern von E-Mail-Inhalten und Extraktionsdaten für Analysezwecke
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="font-medium">Debug-Logging</span>
                <Badge variant={isDebugLoggingEnabled ? "default" : "secondary"}>
                  {isDebugLoggingEnabled ? "Aktiv" : "Inaktiv"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Aktiviert die Speicherung von E-Mail-Daten für Prompt-Optimierung
              </p>
            </div>
            <Switch
              checked={isDebugLoggingEnabled}
              onCheckedChange={toggleDebugLogging}
              disabled={isTogglingDebugLog}
            />
          </div>

          {isDebugLoggingEnabled && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Datenschutz-Hinweis</AlertTitle>
              <AlertDescription>
                Debug-Logging speichert E-Mail-Inhalte, HTML-Code und extrahierte Kontaktdaten in der Datenbank.
                Dies ist nur für Entwicklung und Testing vorgesehen. 
                <strong> In Produktion sollte diese Funktion deaktiviert sein.</strong>
              </AlertDescription>
            </Alert>
          )}

          <div className="text-sm text-muted-foreground">
            <p>Gespeicherte Logs: <strong>{logs.length}</strong></p>
          </div>
        </CardContent>
      </Card>

      <Card className="app-tile app-tile-hover">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="app-icon-box">
              <RefreshCw className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="app-section-title">Extraktions-Statistiken</CardTitle>
              <CardDescription>Logs verwalten, exportieren und Test-Mails auslösen</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={() => setShowDeleteDialog(true)}
              variant="outline" 
              size="sm"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Meine Logs löschen
            </Button>
            <Button
              onClick={loadLogs}
              variant="outline"
              size="sm"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              disabled={logs.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              CSV Export
            </Button>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                max="1000"
                value={testMailCount}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value < 1) {
                    toast.warning("⚠️ Mindestens 1 Test-Mail erforderlich");
                    setTestMailCount(1);
                  } else if (value > 1000) {
                    toast.warning("⚠️ Maximal 1000 Test-Mails erlaubt");
                    setTestMailCount(1000);
                  } else {
                    setTestMailCount(value);
                  }
                }}
                placeholder="Anzahl"
                className="w-24"
              />
              <Button
                onClick={triggerTestMails}
                variant="outline"
                size="sm"
                disabled={isTriggeringTestMails || testMailCount < 1}
              >
                {isTriggeringTestMails ? (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sende {testMailCount} Mails...
                    {testMailsReceived === 0 ? (
                      <Badge variant="secondary" className="ml-2 w-32 justify-center">
                        ⏳ Warte...
                      </Badge>
                    ) : (
                      <Badge variant="default" className="ml-2 w-32 justify-center">
                        📬 {testMailsReceived}/{expectedTestMails}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Test-Mails erhalten
                  </>
                )}
              </Button>
            </div>
          </div>

          {pendingRunIds.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Unanalysierte Logs gefunden</AlertTitle>
              <AlertDescription>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pendingRunIds.map((runId, index) => {
                    const batchSize = logs.filter(log => log.run_id === runId && !log.extracted_data).length;
                    return (
                      <span key={runId} className="text-sm text-muted-foreground">
                        Batch #{index + 1}: {batchSize} Logs
                      </span>
                    );
                  })}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Gesamt</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-success">{stats.successful}</div>
              <div className="text-sm text-muted-foreground">Erfolgreich</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{(stats.avgCompleteness * 100).toFixed(1)}%</div>
              <div className="text-sm text-muted-foreground">Ø Vollständigkeit</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.avgProcessingTime.toFixed(0)}ms</div>
              <div className="text-sm text-muted-foreground">Ø Verarbeitungszeit</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        {/* Log List */}
        <Card className="app-tile app-tile-hover">
          <CardHeader>
            <CardTitle className="app-section-title">Recent Extractions</CardTitle>
            <CardDescription>Tap a row to inspect raw email and AI response</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
            {/* Filter Buttons */}
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
                className="min-w-[120px]"
              >
                Alle ({allLogsCount})
              </Button>
              <Button
                variant={filter === 'production' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('production')}
                className="min-w-[120px]"
              >
                Production ({productionLogsCount})
              </Button>
              <Button
                variant={filter === 'test' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('test')}
                className="min-w-[120px]"
              >
                Test-Mails ({testLogsCount})
              </Button>
            </div>

            {/* Log Entries */}
            {isLoading ? (
              <div className="text-center py-8">Lade Logs...</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Noch keine Logs vorhanden</div>
            ) : (
              <div className="space-y-2 transition-opacity duration-200">
                {logs
                  .filter(log => {
                    if (filter === 'production') return !log.is_test_mail;
                    if (filter === 'test') return log.is_test_mail;
                    return true;
                  })
                  .map(log => (
                  <div
                    key={log.id}
                    className={`p-3 border rounded cursor-pointer hover:bg-accent transition-all duration-200 ${
                      selectedLog?.id === log.id ? 'ring-2 ring-primary' : ''
                    } ${log.is_test_mail ? 'bg-primary/5 border-primary/30' : ''}`}
                    onClick={() => setSelectedLog(log)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{log.sender_email || 'Unbekannt'}</span>
                          {log.is_test_mail && (
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                              Test
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(log.created_at).toLocaleString('de-DE')}
                        </div>
                      </div>
                      <Badge variant={log.extraction_success ? "default" : "destructive"}>
                        {((log.extraction_completeness || 0) * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <div className="text-xs mt-2">
                      {log.fields_extracted}/{log.fields_requested || 'all'} fields • {log.processing_time_ms}ms
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail View */}
        {selectedLog ? (
          <Card className="app-tile app-tile-hover">
            <CardHeader>
              <CardTitle className="app-section-title">Extraction Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="extracted">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="extracted">Extracted Data</TabsTrigger>
                  <TabsTrigger value="email">Email Content</TabsTrigger>
                  <TabsTrigger value="raw">Raw AI Response</TabsTrigger>
                </TabsList>

                <TabsContent value="extracted" className="space-y-2">
                  <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-[500px]">
                    {JSON.stringify(selectedLog.extracted_data as Record<string, any>, null, 2)}
                  </pre>
                </TabsContent>

                <TabsContent value="email" className="space-y-2">
                  <div className="text-sm">
                    <strong>Raw Text:</strong>
                    <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-[200px] mt-2">
                      {selectedLog.email_raw_text || 'Kein Raw Text vorhanden'}
                    </pre>
                  </div>
                  {selectedLog.email_html && (
                    <div className="text-sm mt-4">
                      <strong>HTML:</strong>
                      <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-[200px] mt-2">
                        {selectedLog.email_html.substring(0, 1000)}...
                      </pre>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="raw" className="space-y-2">
                  <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-[500px]">
                    {selectedLog.azure_raw_response || 'Keine Rohdaten vorhanden'}
                  </pre>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex items-center justify-center h-[600px]">
              <div className="text-muted-foreground">Wähle einen Log-Eintrag aus</div>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Logs wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion löscht alle deine Extraction-Logs unwiderruflich aus der Datenbank.
              Dies betrifft nur deine eigenen Logs (gefiltert nach user_email).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMyLogs}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                'Ja, löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
